import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
// !!! IMPORTANT !!!
//
// This file is shared between the zapier/zapier and zapier/next-js repository.
// Any changes made here should be reflected in there as well.
// For more information, see: https://zapier.quip.com/xaKqAOpdhpY2#ZXFACA8Jnp9
import _ from 'lodash';

import ChoiceListItem from 'app/common/components/ChoiceListItem';
import WindowKeydown from 'app/common/components/WindowKeydown';
import { getRef, setRef } from 'app/common/utils/ReactUtils';

import type { AnyComponent } from 'app/common/types';

const noop = () => {};

export type ChoiceListValueType = string | number;

export const ChoiceListValuePropType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
]);

interface ChoiceListItem {
  key?: string;
  isDisabled?: boolean;
  [key: string]: any;
}

export interface ChoiceListTypes {
  Item: AnyComponent;
  hasCustomChoiceHandlers?: boolean;
  itemClassName?: string;
  items: ChoiceListItem[];
  itemValueKey?: string;
  onChoose: (
    value: ChoiceListValueType,
    didConfirm: boolean,
    item: ChoiceListItem
  ) => void;
  shouldWrapAround?: boolean;
  className?: string;
  value?: ChoiceListValueType;
}

interface ChoiceListState {
  isKeyboardControlled: boolean;
}

interface KeyboardHandlers {
  [key: string]: () => void;
}

class ChoiceList extends Component<ChoiceListTypes, ChoiceListState> {
  static defaultProps = {
    hasCustomChoiceHandlers: false,
    itemValueKey: 'value',
  };

  keyboardHandlers: KeyboardHandlers | null = null;

  state: ChoiceListState = {
    isKeyboardControlled: false,
  };

  takeKeyboardControl = () => {
    this.setState({ isKeyboardControlled: true });
  };

  relinquishKeyboardControl = () => {
    this.setState({ isKeyboardControlled: false });
  };

  getKeyboardHandlers(): KeyboardHandlers {
    const wrapWithControl = (fn: () => void) => () => {
      this.takeKeyboardControl();
      fn();
    };
    return {
      up: wrapWithControl(() => this.chooseSibling(-1)),
      down: wrapWithControl(() => this.chooseSibling(1)),
      enter: wrapWithControl(() =>
        this.chooseDirectly(this.getFirstValidValue())
      ),
    };
  }

  getItemValue(item: ChoiceListItem = {}): ChoiceListValueType {
    return item[this.props.itemValueKey || 'value'];
  }

  getIndexOfCurrentValue(): number {
    const { value, items } = this.props;
    return items.map(item => this.getItemValue(item)).indexOf(value);
  }

  // It's possible that the `value` prop could have been from
  // an `item` that was once in `items` but later that `item`
  // and therefore that `value` were filtered out of `items`,
  // in which case we want to default to the first available `item`.
  getFirstValidValue(): ChoiceListValueType {
    const { items, value } = this.props;
    const isValueInItems = items.some(
      item => this.getItemValue(item) === value
    );
    return isValueInItems ? value! : this.getItemValue(items[0]);
  }

  clamp(idx: number): number {
    const firstIdx = 0;
    const lastIdx = this.props.items.length - 1;
    return Math.min(lastIdx, Math.max(idx, firstIdx));
  }

  chooseSibling(offset: number) {
    const curIdx = this.getIndexOfCurrentValue();
    const newIdx = curIdx + offset;
    const nextIdx = this.clamp(newIdx);
    const isAtEndOfList = newIdx !== nextIdx;
    const nextValue = this.getItemValue(this.props.items[nextIdx]);
    const nextItem = this.props.items[nextIdx] || {};
    if (!nextItem.isDisabled) {
      this.chooseIndirectly(nextValue);
    } else if (!isAtEndOfList) {
      this.chooseSibling(offset > 0 ? offset + 1 : offset - 1);
    }
  }

  findItemByValue(value: ChoiceListValueType): ChoiceListItem | undefined {
    return _.find(this.props.items, item => this.getItemValue(item) === value);
  }

  choose(value: ChoiceListValueType, didConfirm: boolean) {
    const item = this.findItemByValue(value);
    if (item && !item.isDisabled) {
      this.props.onChoose(value, didConfirm, item);
    }
  }

  chooseDirectly = (value: ChoiceListValueType) => {
    this.choose(value, true);
  };

  chooseIndirectly = (value: ChoiceListValueType) => {
    this.choose(value, false);
  };

  UNSAFE_componentWillMount() {
    // Attach to component instance so we keep the same object
    // and function instances.
    this.keyboardHandlers = this.getKeyboardHandlers();
  }

  componentWillUnmount() {
    this.keyboardHandlers = null;
  }

  componentDidMount() {
    this.scrollSelectedItemIntoViewIfNeeded();
  }

  componentDidUpdate() {
    this.scrollSelectedItemIntoViewIfNeeded();
  }

  scrollSelectedItemIntoViewIfNeeded() {
    const list = getRef(this, 'list');
    const selectedItem = getRef(this, 'selectedItem');
    if (!selectedItem) {
      return;
    }
    const listNode = ReactDOM.findDOMNode(list) as HTMLElement;
    const listRect = listNode.getBoundingClientRect();
    const itemNode = ReactDOM.findDOMNode(selectedItem) as HTMLElement;
    const itemRect = itemNode.getBoundingClientRect();
    const diffTop = listRect.top - itemRect.top;
    const diffBtm = itemRect.bottom - listRect.bottom;
    // Align top of top item to top of list
    if (diffTop > 0) {
      listNode.scrollTop -= diffTop;
    } else if (diffBtm > 0) {
      // Align bottom of bottom item to bottom of list
      listNode.scrollTop += diffBtm;
    }
  }

  renderItem = (item: ChoiceListItem) => {
    const { value, Item, itemClassName } = this.props;
    const { isKeyboardControlled } = this.state;
    const itemValue = this.getItemValue(item);
    const isSelected = itemValue === value;
    return (
      <ChoiceListItem
        className={itemClassName}
        hasCustomChoiceHandlers={this.props.hasCustomChoiceHandlers}
        isSelected={isSelected}
        Item={Item}
        key={item.key || String(itemValue)}
        {...item}
        onChoose={this.chooseDirectly}
        onSoftChoose={isKeyboardControlled ? noop : this.chooseIndirectly}
        ref={isSelected ? setRef(this, 'selectedItem') : null}
        value={itemValue}
      />
    );
  };

  render() {
    return (
      <WindowKeydown handlers={this.keyboardHandlers}>
        <ul
          className={this.props.className}
          onMouseMove={this.relinquishKeyboardControl}
          ref={setRef(this, 'list')}
          role="menu"
        >
          {this.props.items.map(this.renderItem)}
        </ul>
      </WindowKeydown>
    );
  }
}

export default ChoiceList;
